import { useState } from 'react';
import { Alert, Box, CircularProgress, Tab, Tabs } from '@mui/material';
import type { LayoutBlock } from './types';
import type { RelationPickerTarget } from '../grid/components/dialogs/RelationPickerDialog';
import { useSpecificationForm } from './useSpecificationForm';
import { SpecificationTypePanel } from './SpecificationTypePanel';

type DetailFormSpecificationSectionProps = {
  block: LayoutBlock;
  parentId: string;
  mode: 'add' | 'edit';
  onRegisterRelationTarget?: (target: RelationPickerTarget) => () => void;
  contentKey?: string;
};

export const DetailFormSpecificationSection = ({
  block,
  parentId,
  mode,
  onRegisterRelationTarget,
  contentKey,
}: DetailFormSpecificationSectionProps) => {
  const [activeTab, setActiveTab] = useState(0);

  const {
    data,
    isLoading,
    isError,
    fieldValues,
    setFieldValue,
    handleSave,
    isSavingValues,
    saveValuesError,
    saveSuccess,
  } = useSpecificationForm({
    blockKey: block.key,
    parentId,
    mode,
    contentKey,
    onRegisterRelationTarget,
  });

  if (mode === 'add') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Save the parent record first to add specifications.</Alert>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (isError || !data || data.Code < 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">Failed to load specifications.</Alert>
      </Box>
    );
  }

  if (!data.SpecTypes?.length) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">No specifications found.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Tabs
        value={activeTab}
        onChange={(_, v: number) => setActiveTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}
      >
        {data.SpecTypes.map((st) => (
          <Tab key={st.SpecificationTypeId} label={st.SpecificationTypeName} />
        ))}
      </Tabs>

      {data.SpecTypes.map((st, tabIndex) => {
        const typeFields = data.Data.filter(
          (f) => f.additionalData?.specTypeId === st.SpecificationTypeId
        );

        return (
          <Box key={st.SpecificationTypeId} role="tabpanel" hidden={activeTab !== tabIndex}>
            {activeTab === tabIndex && (
              <SpecificationTypePanel
                fields={typeFields}
                fieldValues={fieldValues}
                onFieldChange={setFieldValue}
                onSave={handleSave}
                isSaving={isSavingValues}
                saveSuccess={saveSuccess}
                saveError={saveValuesError}
              />
            )}
          </Box>
        );
      })}
    </Box>
  );
};
